import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch('http://localhost:5001/api/homeowners/login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (response.ok) {
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('userId', data.userId);
                navigate('/dashboard');
            } else {
                setError(data.message || 'Login failed');
            }
        } catch (err) {
            setError('Error connecting to server');
            console.error('Login error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <LoginContainer>
            <LoginCard>
                <LoginTitle>Welcome Back</LoginTitle>
                
                {error && <ErrorMessage>{error}</ErrorMessage>}
                
                <LoginForm onSubmit={handleLogin}>
                    <InputField
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                    />
                    <InputField
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading}
                    />
                    <LoginButton type="submit" disabled={loading}>
                        {loading ? <LoadingSpinner /> : 'Login'}
                    </LoginButton>
                </LoginForm>

                <EstimatePrompt>
                    <PromptText>
                        Don't have an account yet?
                    </PromptText>
                    <GetEstimateButton
                        onClick={() => navigate('/calculator')}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={loading}
                    >
                        Get Your Free Estimate
                    </GetEstimateButton>
                    <SmallText>
                        Create an account after getting your estimate to save and track your project
                    </SmallText>
                </EstimatePrompt>
            </LoginCard>
        </LoginContainer>
    );
};

// Styled components
const LoginContainer = styled.div`
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: #0A0A0A;
    color: white;
`;

const LoginCard = styled.div`
    background: #1A1A1A;
    padding: 2.5rem;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(255, 87, 34, 0.1);
    width: 100%;
    max-width: 400px;
    border: 1px solid rgba(255, 87, 34, 0.1);
`;

const LoginTitle = styled.h1`
    text-align: center;
    color: white;
    margin-bottom: 2rem;
    font-size: 2rem;
    font-weight: 600;
`;

const LoginForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const InputField = styled.input`
    padding: 1rem;
    border: 2px solid rgba(255, 87, 34, 0.2);
    border-radius: 8px;
    font-size: 1rem;
    background: rgba(255, 255, 255, 0.05);
    color: white;
    transition: all 0.3s ease;

    &:focus {
        outline: none;
        border-color: #FF5722;
        box-shadow: 0 0 10px rgba(255, 87, 34, 0.1);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.5);
    }
`;

const LoginButton = styled.button`
    padding: 1rem;
    background: #FF5722;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background: #F4511E;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(255, 87, 34, 0.2);
    }
`;

const EstimatePrompt = styled.div`
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    text-align: center;
`;

const PromptText = styled.p`
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 1rem;
`;

const GetEstimateButton = styled(motion.button)`
    width: 100%;
    padding: 1rem;
    background: transparent;
    color: #FF5722;
    border: 2px solid #FF5722;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 1rem;
    transition: all 0.3s ease;

    &:hover {
        background: #FF5722;
        color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 15px rgba(255, 87, 34, 0.2);
    }
`;

const SmallText = styled.p`
    color: rgba(255, 255, 255, 0.5);
    font-size: 0.9rem;
    line-height: 1.4;
`;

const ErrorMessage = styled.div`
    color: #FF5722;
    text-align: center;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background: rgba(255, 87, 34, 0.1);
    border-radius: 4px;
    border: 1px solid rgba(255, 87, 34, 0.2);
`;

const LoadingSpinner = styled.div`
    border: 2px solid rgba(255, 87, 34, 0.1);
    border-top: 2px solid #FF5722;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    animation: spin 1s linear infinite;
    margin: 0 auto;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

export default Login;
